import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronUp, 
  Users2,
  User,
  AlertCircle,
  Check,
  X,
  Merge,
  Loader2,
  Calendar,
  MapPin,
  BookmarkPlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confidence badge styling
const ConfidenceBadge = ({ score }) => {
  let style = 'bg-secondary text-muted-foreground border-border';
  let label = 'Low';
  
  if (score >= 85) {
    style = 'bg-red-900/30 text-red-400 border-red-800/50';
    label = 'High';
  } else if (score >= 70) {
    style = 'bg-amber-900/30 text-amber-400 border-amber-800/50';
    label = 'Likely';
  } else if (score >= 50) {
    style = 'bg-blue-900/30 text-blue-400 border-blue-800/50';
    label = 'Possible';
  }
  
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-[0.1em] font-medium ${style}`}>
      {score}% {label}
    </span>
  );
};

// Person info display
const PersonInfo = ({ person, label }) => (
  <div className="flex-1 min-w-0">
    <p className="text-[10px] text-muted-foreground/60 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-foreground/90 truncate">{person.name || 'Unknown'}</p>
    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground/70">
      {person.birth_year && (
        <span className="flex items-center gap-0.5">
          <Calendar className="w-2.5 h-2.5" />
          {person.birth_year}
        </span>
      )}
      {person.birth_place && (
        <span className="flex items-center gap-0.5 truncate max-w-[100px]">
          <MapPin className="w-2.5 h-2.5" />
          {person.birth_place}
        </span>
      )}
    </div>
  </div>
);

// Single Duplicate Card
const DuplicateCard = ({ candidate, treeId, treeName, onMerge, onDismiss, onSaveToJournal, isProcessing, isSavingToJournal }) => {
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  
  const handleMergeClick = () => {
    setSelectedPrimary(candidate.person_a.id);
    setShowMergeDialog(true);
  };
  
  const handleConfirmMerge = () => {
    if (!selectedPrimary) return;
    
    const secondary = selectedPrimary === candidate.person_a.id 
      ? candidate.person_b.id 
      : candidate.person_a.id;
    
    onMerge(selectedPrimary, secondary);
    setShowMergeDialog(false);
  };
  
  const handleConfirmDismiss = () => {
    onDismiss(candidate.id);
    setShowDismissDialog(false);
  };
  
  const details = candidate.matching_factors?.details || [];
  
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="bg-card border-secondary hover:border-amber-900/40 transition-all duration-300">
          <CardContent className="p-4">
            {/* Header with confidence */}
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-amber-900/20 border border-amber-800/30">
                  <Users2 className="w-4 h-4 text-amber-500/80" />
                </div>
                <p className="text-xs text-muted-foreground/80">
                  Potential Duplicate
                </p>
              </div>
              <ConfidenceBadge score={candidate.confidence_score} />
            </div>
            
            {/* Person comparison */}
            <div className="flex items-start gap-3 mb-3 p-2 rounded-lg bg-secondary">
              <PersonInfo person={candidate.person_a} label="Person A" />
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-border text-muted-foreground/60 text-xs shrink-0 mt-3">
                =
              </div>
              <PersonInfo person={candidate.person_b} label="Person B" />
            </div>
            
            {/* Matching reasons */}
            {details.length > 0 && (
              <div className="mb-3 space-y-1">
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Matching factors</p>
                <ul className="text-xs text-muted-foreground/80 space-y-0.5">
                  {details.slice(0, 3).map((detail, idx) => (
                    <li key={idx} className="flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-emerald-500/70" />
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Suggested action */}
            <p className="text-[11px] text-muted-foreground/70 italic mb-3">
              {candidate.suggested_action}
            </p>
            
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleMergeClick}
                disabled={isProcessing}
                className="flex-1 h-8 text-xs bg-emerald-900/20 border-emerald-800/40 text-emerald-400 hover:bg-emerald-900/30 hover:text-emerald-300"
                data-testid={`merge-btn-${candidate.id}`}
              >
                <Merge className="w-3 h-3 mr-1.5" />
                Merge Records
              </Button>
              {onSaveToJournal && treeId && treeName && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSaveToJournal(candidate)}
                  disabled={isProcessing || isSavingToJournal}
                  className="h-8 px-2 text-[10px] border-border text-muted-foreground hover:text-foreground"
                  data-testid={`save-duplicate-to-journal-${candidate.id}`}
                >
                  {isSavingToJournal ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookmarkPlus className="w-3 h-3 mr-1" />}
                  Add to Journal
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDismissDialog(true)}
                disabled={isProcessing}
                className="flex-1 h-8 text-xs border-border text-muted-foreground hover:bg-secondary"
                data-testid={`dismiss-btn-${candidate.id}`}
              >
                <X className="w-3 h-3 mr-1.5" />
                Different People
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      {/* Dismiss confirmation dialog */}
      <Dialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Confirm These Are Different People</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This will mark these individuals as separate people in your family tree.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary border border-border">
              <User className="w-5 h-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">{candidate.person_a.name}</p>
                <p className="text-xs text-muted-foreground">
                  {candidate.person_a.birth_year && `Born ${candidate.person_a.birth_year}`}
                  {candidate.person_a.birth_place && ` • ${candidate.person_a.birth_place}`}
                </p>
              </div>
            </div>
            
            <div className="flex justify-center">
              <span className="text-xs text-muted-foreground/60">is NOT the same person as</span>
            </div>
            
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary border border-border">
              <User className="w-5 h-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">{candidate.person_b.name}</p>
                <p className="text-xs text-muted-foreground">
                  {candidate.person_b.birth_year && `Born ${candidate.person_b.birth_year}`}
                  {candidate.person_b.birth_place && ` • ${candidate.person_b.birth_place}`}
                </p>
              </div>
            </div>
            
            <p className="text-[11px] text-muted-foreground/70 italic text-center">
              This action can be helpful if similar names appear across your tree.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDismissDialog(false)}
              className="border-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDismiss}
              disabled={isProcessing}
              className="bg-stone-700 hover:bg-muted-foreground"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Mark as Different
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Merge confirmation dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Merge Records</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Select which record to keep as the primary. The other will be archived.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <p className="text-xs text-muted-foreground/70">
              All stories, documents, and relationships will be transferred to the primary record.
            </p>
            
            {/* Person A option */}
            <button
              onClick={() => setSelectedPrimary(candidate.person_a.id)}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                selectedPrimary === candidate.person_a.id
                  ? 'border-emerald-600 bg-emerald-900/20'
                  : 'border-border bg-secondary hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium text-foreground">{candidate.person_a.name}</span>
                {selectedPrimary === candidate.person_a.id && (
                  <span className="ml-auto text-[10px] text-emerald-400 uppercase">Keep</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {candidate.person_a.birth_year && `Born ${candidate.person_a.birth_year}`}
                {candidate.person_a.birth_place && ` • ${candidate.person_a.birth_place}`}
              </p>
            </button>
            
            {/* Person B option */}
            <button
              onClick={() => setSelectedPrimary(candidate.person_b.id)}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                selectedPrimary === candidate.person_b.id
                  ? 'border-emerald-600 bg-emerald-900/20'
                  : 'border-border bg-secondary hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium text-foreground">{candidate.person_b.name}</span>
                {selectedPrimary === candidate.person_b.id && (
                  <span className="ml-auto text-[10px] text-emerald-400 uppercase">Keep</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {candidate.person_b.birth_year && `Born ${candidate.person_b.birth_year}`}
                {candidate.person_b.birth_place && ` • ${candidate.person_b.birth_place}`}
              </p>
            </button>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMergeDialog(false)}
              className="border-border"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMerge}
              disabled={!selectedPrimary || isProcessing}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Merge className="w-4 h-4 mr-2" />
              )}
              Confirm Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main Duplicate Candidates Panel
const DuplicateCandidatesPanel = ({ treeId, treeName, onRefresh }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [savingToJournalId, setSavingToJournalId] = useState(null);

  const handleSaveToJournal = async (candidate) => {
    if (!treeId || !treeName) return;
    setSavingToJournalId(candidate.id);
    try {
      const title = `Potential duplicate: ${candidate.person_a?.name || 'Person A'} / ${candidate.person_b?.name || 'Person B'}`;
      await axios.post(`${API}/investigations`, {
        tree_id: treeId,
        tree_name: treeName,
        insight_id: candidate.id,
        insight_type: 'duplicate',
        insight_title: title,
        insight_description: candidate.suggested_action || 'Potential duplicate individuals detected',
        search_params: {
          person_a_id: candidate.person_a?.id,
          person_b_id: candidate.person_b?.id
        },
        linked_individual_ids: [candidate.person_a?.id, candidate.person_b?.id].filter(Boolean),
        notes: null
      });
      toast.success('Duplicate candidate added to Research Journal');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already saved')) {
        toast.info('This duplicate is already in your Research Journal');
      } else {
        toast.error(err.response?.data?.detail || 'Could not add to journal');
      }
    } finally {
      setSavingToJournalId(null);
    }
  };
  
  const fetchCandidates = async () => {
    if (!treeId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/trees/${treeId}/duplicates`);
      setCandidates(response.data.candidates || []);
    } catch (err) {
      console.error('Failed to fetch duplicate candidates:', err);
      setError('Unable to analyze duplicates');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCandidates();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when treeId changes only
  }, [treeId]);
  
  const handleMerge = async (primaryId, secondaryId) => {
    setIsProcessing(true);
    
    try {
      const response = await axios.post(
        `${API}/trees/${treeId}/merge?primary_id=${primaryId}&secondary_id=${secondaryId}`
      );
      
      toast.success(response.data.message, { duration: 3000 });
      
      // Refresh candidates
      fetchCandidates();
      
      // Notify parent to refresh tree data
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Merge failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to merge records');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleDismiss = async (candidateId) => {
    setIsProcessing(true);
    
    try {
      await axios.post(`${API}/trees/${treeId}/duplicates/${candidateId}/dismiss`);
      
      toast.success('Marked as different people', { duration: 2000 });
      
      // Remove from local list
      setCandidates(prev => prev.filter(c => c.id !== candidateId));
    } catch (err) {
      console.error('Dismiss failed:', err);
      toast.error('Failed to dismiss duplicate');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Don't render if no candidates and not loading
  if (!loading && candidates.length === 0) {
    return null;
  }
  
  return (
    <div className="w-full" data-testid="duplicate-candidates-panel">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className="bg-gradient-to-br from-card to-stone-950/80 border-secondary shadow-lg">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-secondary transition-colors py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-amber-900/30 border border-amber-800/40">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground/90">
                      Duplicate Detection
                    </h3>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                      Potential duplicate ancestors
                    </p>
                  </div>
                  {candidates.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-900/30 text-amber-400 border border-amber-800/40">
                      {candidates.length}
                    </span>
                  )}
                </div>
                {isOpen ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground/60" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground/60" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="pt-0 px-4 pb-4">
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 text-amber-500/60 animate-spin" />
                  <span className="ml-2 text-xs text-muted-foreground">Analyzing for duplicates...</span>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <p className="text-xs text-muted-foreground/70">{error}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[11px] text-muted-foreground/70 italic">
                    These individuals may represent the same ancestor.
                  </p>
                  
                  <AnimatePresence mode="popLayout">
                    {candidates.map((candidate, index) => (
                      <motion.div
                        key={candidate.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <DuplicateCard 
                          candidate={candidate} 
                          treeId={treeId}
                          treeName={treeName}
                          onMerge={handleMerge}
                          onDismiss={handleDismiss}
                          onSaveToJournal={handleSaveToJournal}
                          isProcessing={isProcessing}
                          isSavingToJournal={savingToJournalId === candidate.id}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  {/* Footer note */}
                  <p className="text-[10px] text-muted-foreground/50 text-center pt-2 italic">
                    Review carefully before merging
                  </p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default DuplicateCandidatesPanel;
